import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, UserPlus, FileText, Briefcase, Megaphone, Mail,
  RefreshCw, Download, LogOut, KeyRound, Ban, Trash2, Upload, Plus, ChevronRight,
} from "lucide-react";
import { LOGO_URL, DIVISIONI } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATI = [
  { value: "nuova", label: "Nuova" },
  { value: "contattata", label: "Contattata" },
  { value: "in-trattativa", label: "In trattativa" },
  { value: "chiusa", label: "Chiusa" },
];

const statoStyle = (s) =>
  s === "chiusa"
    ? "border-[#A5A5A5]/40 text-[#A5A5A5]"
    : s === "in-trattativa"
    ? "border-[#E5C776] text-[#E5C776]"
    : "border-[#8F6B32] text-[#E5C776]";

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return d;
  }
};

const inputCls =
  "w-full bg-[#1D1D1D] border border-[#A5A5A5]/25 text-[#F5F2EB] placeholder-[#A5A5A5]/50 px-4 py-3 text-sm font-light focus:outline-none focus:border-[#E5C776] focus:ring-1 focus:ring-[#E5C776] transition-colors duration-300";

const btnGold =
  "inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] disabled:opacity-50 transition-colors duration-300";

const btnDanger =
  "inline-flex items-center gap-2 px-4 py-2 border border-red-900/60 text-red-300 text-[10px] tracking-[0.25em] uppercase hover:bg-red-900/30 disabled:opacity-40 transition-colors duration-300";

const btnOutline =
  "inline-flex items-center gap-2 px-5 py-2 border border-[#C6A45D]/60 text-[#C6A45D] text-[10px] tracking-[0.25em] uppercase hover:bg-[#C6A45D] hover:text-[#080808] disabled:opacity-40 transition-colors duration-300";

const cardCls =
  "bg-[#141414] border border-[#C6A45D]/15 hover:border-[#C6A45D]/40 transition-colors duration-300";

const Empty = ({ text }) => (
  <div className="bg-[#141414] border border-[#C6A45D]/15 p-10 text-center text-sm text-[#A5A5A5] font-light">
    {text}
  </div>
);

const DestinatariPicker = ({ partners, selected, onChange, testid }) => {
  const toggle = (email) =>
    onChange(selected.includes(email) ? selected.filter((e) => e !== email) : [...selected, email]);
  return (
    <div data-testid={testid}>
      <label className="block eyebrow mb-3 !text-[10px]">Destinatari</label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange([])}
          data-testid={`${testid}-tutti`}
          className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
            selected.length === 0
              ? "bg-[#C6A45D] text-[#080808] border-[#C6A45D] font-semibold"
              : "border-[#A5A5A5]/30 text-[#A5A5A5] hover:border-[#C6A45D] hover:text-[#C6A45D]"
          }`}
        >
          Tutti i partner
        </button>
        {partners.map((p) => (
          <button
            type="button"
            key={p.email}
            onClick={() => toggle(p.email)}
            data-testid={`${testid}-${p.email}`}
            className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
              selected.includes(p.email)
                ? "bg-[#C6A45D] text-[#080808] border-[#C6A45D] font-semibold"
                : "border-[#A5A5A5]/30 text-[#A5A5A5] hover:border-[#C6A45D] hover:text-[#C6A45D]"
            }`}
          >
            {p.name}
          </button>
        ))}
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-[#C6A45D] mt-2 tracking-wider">
          Visibile solo a {selected.length} partner selezionat{selected.length === 1 ? "o" : "i"}
        </p>
      )}
    </div>
  );
};

const DestLabel = ({ destinatari }) => (
  <span className="inline-block text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 border border-[#A5A5A5]/30 text-[#A5A5A5]">
    {destinatari && destinatari.length > 0 ? `${destinatari.length} partner` : "Tutti i partner"}
  </span>
);

const StatCard = ({ icon: Icon, value, label, testid }) => (
  <div data-testid={testid} className="bg-[#141414] border border-[#C6A45D]/20 p-6 flex items-center gap-5">
    <div className="w-11 h-11 border border-[#C6A45D]/40 flex items-center justify-center shrink-0">
      <Icon size={19} className="text-[#C6A45D]" />
    </div>
    <div>
      <p className="font-display text-3xl font-light text-[#F5F2EB]">{value}</p>
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#A5A5A5] mt-1">{label}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [sezione, setSezione] = useState("panoramica");
  const [me, setMe] = useState(null);
  const [partnership, setPartnership] = useState([]);
  const [contatti, setContatti] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [opportunita, setOpportunita] = useState([]);
  const [comunicazioni, setComunicazioni] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [tempCreds, setTempCreds] = useState({});
  const [approving, setApproving] = useState(null);
  const [docTitolo, setDocTitolo] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [docDest, setDocDest] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [oppForm, setOppForm] = useState({ titolo: "", divisione: "", descrizione: "", scadenza: "" });
  const [oppDest, setOppDest] = useState([]);
  const [savingOpp, setSavingOpp] = useState(false);
  const [comForm, setComForm] = useState({ titolo: "", testo: "" });
  const [comDest, setComDest] = useState([]);
  const [savingCom, setSavingCom] = useState(false);
  const [partnerForm, setPartnerForm] = useState({ nome: "", email: "", password: "", azienda: "" });
  const [creatingPartner, setCreatingPartner] = useState(false);

  const authErr = (e) => {
    if (e?.response?.status === 401) { navigate("/admin/login"); return true; }
    return false;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, p, c, d, o, cm, pa] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/partnership`, { withCredentials: true }),
        axios.get(`${API}/contatti`, { withCredentials: true }),
        axios.get(`${API}/documenti`, { withCredentials: true }),
        axios.get(`${API}/opportunita`, { withCredentials: true }),
        axios.get(`${API}/comunicazioni`, { withCredentials: true }),
        axios.get(`${API}/partner`, { withCredentials: true }),
      ]);
      setMe(m.data);
      setPartnership(p.data);
      setNoteDraft(Object.fromEntries(p.data.map((r) => [r.id, r.note || ""])));
      setContatti(c.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setDocumenti(d.data);
      setOpportunita(o.data);
      setComunicazioni(cm.data);
      setPartners(pa.data);
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Impossibile caricare i dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate("/admin/login");
    }
  };

  const segnaLetta = async (r) => {
    if (r.letta) return;
    setPartnership((prev) => prev.map((x) => (x.id === r.id ? { ...x, letta: true } : x)));
    try {
      await axios.patch(`${API}/partnership/${r.id}/letta`, {}, { withCredentials: true });
    } catch (e) {
      authErr(e);
    }
  };

  const cambiaStato = async (id, stato) => {    try {
      const { data } = await axios.patch(`${API}/partnership/${id}/stato`, { stato }, { withCredentials: true });
      setPartnership((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Stato aggiornato");
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Aggiornamento non riuscito");
    }
  };

  const salvaNote = async (id) => {
    setSavingNote(id);
    try {
      const { data } = await axios.patch(`${API}/partnership/${id}/note`, { note: noteDraft[id] ?? "" }, { withCredentials: true });
      setPartnership((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Nota salvata");
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Salvataggio nota non riuscito");
    } finally {
      setSavingNote(null);
    }
  };

  const approvaAccesso = async (id) => {
    setApproving(id);
    try {
      const { data } = await axios.post(`${API}/partnership/${id}/approva`, {}, { withCredentials: true });
      setTempCreds((prev) => ({ ...prev, [id]: data }));
      setPartnership((prev) => prev.map((r) => (r.id === id ? { ...r, account_status: "approvato", stato: "in-trattativa" } : r)));
      toast.success("Accesso partner attivato", { description: "Comunica le credenziali temporanee al partner." });
      load();
    } catch (e) {
      if (authErr(e)) return;
      toast.error(e?.response?.data?.detail || "Approvazione non riuscita");
    } finally {
      setApproving(null);
    }
  };

  const revocaAccesso = async (id) => {
    setApproving(id);
    try {
      await axios.post(`${API}/partnership/${id}/revoca`, {}, { withCredentials: true });
      setTempCreds((prev) => { const n = { ...prev }; delete n[id]; return n; });
      setPartnership((prev) => prev.map((r) => (r.id === id ? { ...r, account_status: null } : r)));
      toast.success("Accesso revocato");
      load();
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Revoca non riuscita");
    } finally {
      setApproving(null);
    }
  };

  const eliminaCandidatura = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa candidatura? L'eventuale accesso partner collegato verrà revocato.")) return;
    try {
      await axios.delete(`${API}/partnership/${id}`, { withCredentials: true });
      setPartnership((prev) => prev.filter((r) => r.id !== id));
      setExpandedId(null);
      toast.success("Candidatura eliminata");
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Eliminazione non riuscita");
    }
  };

  const creaPartner = async (e) => {
    e.preventDefault();
    setCreatingPartner(true);
    try {
      await axios.post(`${API}/partner`, { ...partnerForm, azienda: partnerForm.azienda || null }, { withCredentials: true });
      toast.success("Partner creato", { description: `${partnerForm.email} può accedere all'Area Riservata.` });
      setPartnerForm({ nome: "", email: "", password: "", azienda: "" });
      load();
    } catch (err) {
      if (authErr(err)) return;
      toast.error(err?.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setCreatingPartner(false);
    }
  };

  const eliminaPartner = async (email) => {
    if (!window.confirm(`Revocare l'accesso a ${email}?`)) return;
    try {
      await axios.delete(`${API}/partner?email=${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success("Accesso partner revocato");
      load();
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Revoca non riuscita");
    }
  };

  const uploadDocumento = async (e) => {
    e.preventDefault();
    if (!docFile || !docTitolo.trim()) { toast.error("Inserisci titolo e file"); return; }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("titolo", docTitolo.trim());
      fd.append("file", docFile);
      if (docDest.length > 0) fd.append("destinatari", docDest.join(","));
      const { data } = await axios.post(`${API}/documenti`, fd, { withCredentials: true });
      setDocumenti((prev) => [data, ...prev]);
      setDocTitolo("");
      setDocFile(null);
      setDocDest([]);
      e.target.reset();
      toast.success("Documento caricato", { description: "Ora visibile nell'Area Riservata dei partner." });
    } catch (err) {
      if (authErr(err)) return;
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDocumento = async (id) => {
    try {
      await axios.delete(`${API}/documenti/${id}`, { withCredentials: true });
      setDocumenti((prev) => prev.filter((d) => d.id !== id));
      toast.success("Documento eliminato");
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Eliminazione non riuscita");
    }
  };

  const creaOpportunita = async (e) => {
    e.preventDefault();
    setSavingOpp(true);
    try {
      const { data } = await axios.post(
        `${API}/opportunita`,
        { ...oppForm, divisione: oppForm.divisione || null, scadenza: oppForm.scadenza || null, destinatari: oppDest },
        { withCredentials: true }
      );
      setOpportunita((prev) => [data, ...prev]);
      setOppForm({ titolo: "", divisione: "", descrizione: "", scadenza: "" });
      setOppDest([]);
      toast.success("Opportunità pubblicata");
    } catch (err) {
      if (authErr(err)) return;
      toast.error("Pubblicazione non riuscita");
    } finally {
      setSavingOpp(false);
    }
  };

  const deleteOpportunita = async (id) => {
    try {
      await axios.delete(`${API}/opportunita/${id}`, { withCredentials: true });
      setOpportunita((prev) => prev.filter((o) => o.id !== id));
      toast.success("Opportunità eliminata");
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Eliminazione non riuscita");
    }
  };

  const creaComunicazione = async (e) => {
    e.preventDefault();
    setSavingCom(true);
    try {
      const { data } = await axios.post(`${API}/comunicazioni`, { ...comForm, destinatari: comDest }, { withCredentials: true });
      setComunicazioni((prev) => [data, ...prev]);
      setComForm({ titolo: "", testo: "" });
      setComDest([]);
      toast.success("Comunicazione pubblicata", { description: "Visibile nell'Area Riservata dei partner." });
    } catch (err) {
      if (authErr(err)) return;
      toast.error("Pubblicazione non riuscita");
    } finally {
      setSavingCom(false);
    }
  };

  const deleteComunicazione = async (id) => {
    try {
      await axios.delete(`${API}/comunicazioni/${id}`, { withCredentials: true });
      setComunicazioni((prev) => prev.filter((c) => c.id !== id));
      toast.success("Comunicazione eliminata");
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Eliminazione non riuscita");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/partnership-export`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "candidature_partner.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (authErr(e)) return;
      toast.error("Export non riuscito");
    }
  };

  const partnershipFiltered = partnership.filter(
    (r) => !filtroStato || (r.stato || "nuova") === filtroStato
  );

  const MENU = [
    { id: "panoramica", label: "Panoramica", icon: LayoutDashboard },
    { id: "candidature", label: "Candidature", icon: Users, badge: partnership.filter((r) => !r.letta).length },
    { id: "partner", label: "Partner", icon: UserPlus, badge: partners.length },
    { id: "documenti", label: "Documenti", icon: FileText, badge: documenti.length },
    { id: "opportunita", label: "Opportunità", icon: Briefcase, badge: opportunita.length },
    { id: "comunicazioni", label: "Comunicazioni", icon: Megaphone, badge: comunicazioni.length },
    { id: "messaggi", label: "Messaggi", icon: Mail, badge: contatti.length },
  ];

  return (
    <main data-testid="admin-page" className="bg-[#080808] min-h-screen pt-20">
      <div className="flex">
        {/* SIDEBAR */}
        <aside
          data-testid="admin-sidebar"
          className="hidden lg:flex flex-col w-64 shrink-0 bg-[#0D0D0D] border-r border-[#C6A45D]/15 sticky top-20 h-[calc(100vh-5rem)]"
        >
          <div className="p-6 border-b border-[#C6A45D]/10 flex items-center gap-3">
            <img src={LOGO_URL} alt="Billions Group" className="h-9 w-auto" />
            <div>
              <p className="font-display text-sm text-[#F5F2EB] tracking-wide">Dashboard</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#C6A45D]">Admin</p>
            </div>
          </div>
          <nav className="flex-1 py-4 overflow-y-auto">
            {MENU.map((m) => (
              <button
                key={m.id}
                onClick={() => setSezione(m.id)}
                data-testid={`admin-menu-${m.id}`}
                className={`w-full flex items-center gap-3 px-6 py-3.5 text-xs tracking-[0.2em] uppercase transition-colors duration-300 border-l-2 ${
                  sezione === m.id
                    ? "border-[#C6A45D] text-[#E5C776] bg-[#C6A45D]/5"
                    : "border-transparent text-[#A5A5A5] hover:text-[#F5F2EB] hover:bg-[#141414]"
                }`}
              >
                <m.icon size={16} className="shrink-0" />
                <span className="flex-1 text-left">{m.label}</span>
                {m.badge > 0 && (
                  <span className="text-[10px] px-2 py-0.5 border border-[#C6A45D]/40 text-[#C6A45D]">{m.badge}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="p-6 border-t border-[#C6A45D]/10">
            <p className="text-xs text-[#A5A5A5] truncate mb-3">{me?.email}</p>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#A5A5A5]/30 text-[#A5A5A5] text-[10px] tracking-[0.25em] uppercase hover:border-[#C6A45D] hover:text-[#C6A45D] transition-colors duration-300"
            >
              <LogOut size={13} /> Esci
            </button>
          </div>
        </aside>

        {/* CONTENT */}
        <div className="flex-1 min-w-0">
          {/* Mobile menu */}
          <div className="lg:hidden flex gap-2 overflow-x-auto px-4 py-3 border-b border-[#C6A45D]/15 bg-[#0D0D0D]" data-testid="admin-mobile-menu">
            {MENU.map((m) => (
              <button
                key={m.id}
                onClick={() => setSezione(m.id)}
                className={`flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.2em] uppercase whitespace-nowrap border transition-colors duration-300 ${
                  sezione === m.id ? "border-[#C6A45D] text-[#E5C776]" : "border-[#A5A5A5]/20 text-[#A5A5A5]"
                }`}
              >
                <m.icon size={13} /> {m.label}
              </button>
            ))}
          </div>

          <div className="p-6 lg:p-10">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <h1 className="font-display text-3xl font-light text-[#F5F2EB] capitalize">
                {MENU.find((m) => m.id === sezione)?.label}
              </h1>
              <div className="flex gap-3">
                <button onClick={exportCsv} data-testid="admin-export-button" className={btnGold}>
                  <Download size={14} /> Esporta Excel
                </button>
                <button onClick={load} data-testid="admin-refresh-button" className={btnOutline}>
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
                </button>
                <button onClick={logout} className={`${btnOutline} lg:hidden`}>
                  <LogOut size={14} />
                </button>
              </div>
            </div>

            {/* PANORAMICA */}
            {sezione === "panoramica" && (
              <div data-testid="admin-sezione-panoramica">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                  <StatCard icon={Users} value={partnership.length} label="Candidature totali" testid="admin-stat-partnership" />
                  <StatCard icon={UserPlus} value={partners.length} label="Partner attivi" testid="admin-stat-partner" />
                  <StatCard icon={Mail} value={contatti.length} label="Messaggi contatti" testid="admin-stat-contatti" />
                  <StatCard icon={FileText} value={documenti.length} label="Documenti partner" testid="admin-stat-documenti" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-8">
                  <div className={cardCls + " p-6"}>
                    <p className="eyebrow !text-[10px] mb-5">Ultime candidature</p>
                    {partnership.length === 0 ? (
                      <p className="text-sm text-[#A5A5A5] font-light">Nessuna candidatura ricevuta.</p>
                    ) : (
                      <div className="space-y-3">
                        {partnership.slice(0, 5).map((r) => (
                          <button
                            key={r.id}
                            onClick={() => { setSezione("candidature"); setExpandedId(r.id); segnaLetta(r); }}
                            className="w-full flex items-center justify-between gap-4 py-2 border-b border-[#C6A45D]/10 last:border-0 text-left group"
                          >
                            <div className="min-w-0">
                              <p className="text-sm text-[#F5F2EB] truncate group-hover:text-[#E5C776] transition-colors duration-300">{r.nome}</p>
                              <p className="text-xs text-[#A5A5A5]/70 truncate">{r.email}</p>
                            </div>
                            <span className={`text-[10px] tracking-[0.15em] uppercase border px-2.5 py-1 shrink-0 ${statoStyle(r.stato || "nuova")}`}>
                              {STATI.find((s) => s.value === (r.stato || "nuova"))?.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={cardCls + " p-6"}>
                    <p className="eyebrow !text-[10px] mb-5">Candidature per stato</p>
                    <div className="space-y-4">
                      {STATI.map((s) => {
                        const n = partnership.filter((r) => (r.stato || "nuova") === s.value).length;
                        const pct = partnership.length ? Math.round((n / partnership.length) * 100) : 0;
                        return (
                          <div key={s.value}>
                            <div className="flex justify-between text-xs mb-1.5">
                              <span className="tracking-[0.15em] uppercase text-[#A5A5A5]">{s.label}</span>
                              <span className="text-[#F5F2EB]">{n}</span>
                            </div>
                            <div className="h-1 bg-[#1D1D1D]">
                              <div
                                className="h-1 bg-gradient-to-r from-[#8F6B32] via-[#C6A45D] to-[#E5C776] transition-[width] duration-700"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="gold-line mt-8 mb-5 opacity-50" />
                    <p className="eyebrow !text-[10px] mb-4">Ultima comunicazione</p>
                    {comunicazioni[0] ? (
                      <div>
                        <p className="text-sm text-[#F5F2EB]">{comunicazioni[0].titolo}</p>
                        <p className="text-xs text-[#A5A5A5]/70 mt-1">{formatDate(comunicazioni[0].created_at)}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-[#A5A5A5] font-light">Nessuna comunicazione pubblicata.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CANDIDATURE */}
            {sezione === "candidature" && (
              <div data-testid="admin-sezione-candidature">
                <div data-testid="admin-filters" className="mb-6 flex flex-wrap items-center gap-4">
                  <select
                    value={filtroStato}
                    onChange={(e) => setFiltroStato(e.target.value)}
                    data-testid="admin-filter-stato"
                    className="bg-[#1D1D1D] border border-[#A5A5A5]/25 text-[#F5F2EB] px-4 py-3 text-xs tracking-[0.15em] uppercase cursor-pointer focus:outline-none focus:border-[#E5C776] transition-colors duration-300"
                  >
                    <option value="">Tutti gli stati</option>
                    {STATI.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  {filtroStato && (
                    <button
                      onClick={() => setFiltroStato("")}
                      data-testid="admin-filter-reset"
                      className="text-xs tracking-[0.2em] uppercase text-[#A5A5A5] hover:text-[#C6A45D] transition-colors duration-300 underline underline-offset-4"
                    >
                      Azzera filtri
                    </button>
                  )}
                  <span className="text-xs text-[#A5A5A5]/70 tracking-wider ml-auto">
                    {partnershipFiltered.length} di {partnership.length} candidature
                  </span>
                </div>
                {partnership.length === 0 && !loading ? (
                  <Empty text="Nessuna candidatura ricevuta finora." />
                ) : partnershipFiltered.length === 0 ? (
                  <Empty text="Nessuna candidatura corrisponde ai filtri selezionati." />
                ) : (
                  <div className="space-y-3" data-testid="admin-partnership-list">
                    {partnershipFiltered.map((r) => {
                      const open = expandedId === r.id;
                      return (
                        <div key={r.id} data-testid={`admin-partnership-${r.id}`} className={cardCls}>
                          <div
                            className="flex flex-wrap items-center gap-4 p-5 sm:p-6 cursor-pointer select-none"
                            onClick={() => { setExpandedId(open ? null : r.id); if (!open) segnaLetta(r); }}
                            data-testid={`admin-partnership-toggle-${r.id}`}
                          >
                            <ChevronRight size={18} className={`text-[#C6A45D] shrink-0 transition-transform duration-300 ${open ? "rotate-90" : ""}`} />
                            <div className="flex-1 min-w-[220px]">
                              <p className="font-display text-lg text-[#F5F2EB] flex items-center gap-2">
                                {r.nome}
                                {!r.letta && (
                                  <span data-testid={`admin-unread-dot-${r.id}`} className="w-2 h-2 rounded-full bg-[#E5C776] shrink-0" title="Non letta" />
                                )}
                              </p>
                              <p className="text-xs text-[#A5A5A5] mt-0.5">
                                {r.email}
                                {r.azienda ? ` · ${r.azienda}` : ""}
                                {r.account_status === "approvato" ? " · Accesso attivo" : ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                              <select
                                value={r.stato || "nuova"}
                                onChange={(e) => cambiaStato(r.id, e.target.value)}
                                data-testid={`admin-stato-select-${r.id}`}
                                className={`bg-[#1D1D1D] border px-3 py-1.5 text-xs tracking-[0.15em] uppercase cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#E5C776] transition-colors duration-300 ${statoStyle(r.stato || "nuova")}`}
                              >
                                {STATI.map((s) => (
                                  <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                              </select>
                              <span className="text-xs text-[#A5A5A5]/70 hidden sm:inline">{formatDate(r.created_at)}</span>
                            </div>
                          </div>
                          {open && (
                            <div className="px-5 sm:px-6 pb-6 pt-5 border-t border-[#C6A45D]/10">
                              <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs text-[#A5A5A5]">
                                {r.telefono && <span>Tel: <span className="text-[#F5F2EB]">{r.telefono}</span></span>}
                                {r.divisione && <span className="tracking-[0.2em] uppercase text-[#C6A45D]/80">Business Unit: {r.divisione}</span>}
                                {r.fascia_investimento && <span className="tracking-[0.15em] uppercase text-[#C6A45D]/80">{r.fascia_investimento}</span>}
                              </div>
                              <p className="mt-4 text-sm text-[#A5A5A5] font-light leading-relaxed">{r.messaggio}</p>
                              <div className="mt-5 pt-5 border-t border-[#C6A45D]/10">
                                <label className="block eyebrow mb-3 !text-[10px]">Accesso Area Riservata</label>
                                {r.account_status === "approvato" ? (
                                  <div className="flex flex-wrap items-center gap-4">
                                    <span className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#E5C776]">
                                      <KeyRound size={14} /> Accesso attivo
                                    </span>
                                    <button onClick={() => revocaAccesso(r.id)} disabled={approving === r.id} data-testid={`admin-revoca-${r.id}`} className={btnDanger}>
                                      <Ban size={13} /> Revoca accesso
                                    </button>
                                  </div>
                                ) : (
                                  <button onClick={() => approvaAccesso(r.id)} disabled={approving === r.id} data-testid={`admin-approva-${r.id}`} className={btnGold + " !px-5 !py-2 !text-[10px]"}>
                                    <KeyRound size={13} /> {approving === r.id ? "Attivazione..." : "Approva accesso"}
                                  </button>
                                )}
                                {tempCreds[r.id] && (
                                  <div data-testid={`admin-temp-creds-${r.id}`} className="mt-4 bg-[#080808] border border-[#E5C776]/40 p-4 text-sm">
                                    <p className="text-xs tracking-[0.2em] uppercase text-[#C6A45D] mb-2">Credenziali temporanee (mostrate una sola volta)</p>
                                    <p className="text-[#F5F2EB] font-light">Email: <span className="text-[#E5C776]">{tempCreds[r.id].email}</span></p>
                                    <p className="text-[#F5F2EB] font-light">Password: <span className="text-[#E5C776] font-mono">{tempCreds[r.id].password}</span></p>
                                  </div>
                                )}
                              </div>
                              <div className="mt-5 pt-5 border-t border-[#C6A45D]/10">
                                <label className="block eyebrow mb-3 !text-[10px]">Note interne (solo team)</label>
                                <textarea
                                  value={noteDraft[r.id] ?? ""}
                                  onChange={(e) => setNoteDraft((prev) => ({ ...prev, [r.id]: e.target.value }))}
                                  data-testid={`admin-note-${r.id}`}
                                  rows={2}
                                  placeholder="Appunti riservati su questa candidatura..."
                                  className={inputCls + " resize-none"}
                                />
                                <div className="mt-3 flex flex-wrap items-center gap-3">
                                  <button
                                    onClick={() => salvaNote(r.id)}
                                    disabled={savingNote === r.id || (noteDraft[r.id] ?? "") === (r.note || "")}
                                    data-testid={`admin-note-save-${r.id}`}
                                    className={btnOutline}
                                  >
                                    {savingNote === r.id ? "Salvataggio..." : "Salva nota"}
                                  </button>
                                  <button onClick={() => eliminaCandidatura(r.id)} data-testid={`admin-elimina-${r.id}`} className={btnDanger + " ml-auto"}>
                                    <Trash2 size={13} /> Elimina candidatura
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* PARTNER */}
            {sezione === "partner" && (
              <div data-testid="admin-sezione-partner">
                <form onSubmit={creaPartner} data-testid="admin-partner-form" className={cardCls + " p-6 sm:p-8 mb-6"}>
                  <p className="text-sm text-[#A5A5A5] font-light mb-6">
                    Crea un accesso all'Area Riservata senza passare dalla candidatura.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="np-nome" className="block eyebrow mb-3 !text-[10px]">Nome e Cognome *</label>
                      <input id="np-nome" data-testid="admin-partner-nome" required minLength={2} value={partnerForm.nome} onChange={(e) => setPartnerForm({ ...partnerForm, nome: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="np-email" className="block eyebrow mb-3 !text-[10px]">Email *</label>
                      <input id="np-email" data-testid="admin-partner-email" type="email" required value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="np-password" className="block eyebrow mb-3 !text-[10px]">Password temporanea * (min. 8 caratteri)</label>
                      <input id="np-password" data-testid="admin-partner-password" type="text" required minLength={8} value={partnerForm.password} onChange={(e) => setPartnerForm({ ...partnerForm, password: e.target.value })} className={inputCls + " font-mono"} />
                    </div>
                    <div>
                      <label htmlFor="np-azienda" className="block eyebrow mb-3 !text-[10px]">Azienda</label>
                      <input id="np-azienda" data-testid="admin-partner-azienda" value={partnerForm.azienda} onChange={(e) => setPartnerForm({ ...partnerForm, azienda: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  <button type="submit" disabled={creatingPartner} data-testid="admin-partner-submit" className={btnGold + " mt-6"}>
                    <UserPlus size={14} /> {creatingPartner ? "Creazione..." : "Crea partner"}
                  </button>
                </form>
                {partners.length === 0 && !loading ? (
                  <Empty text="Nessun partner attivo." />
                ) : (
                  <div className="space-y-3" data-testid="admin-partners-list">
                    {partners.map((p) => (
                      <div key={p.email} className={cardCls + " p-5 flex flex-wrap items-center gap-4"}>
                        <div className="w-10 h-10 border border-[#C6A45D]/40 flex items-center justify-center shrink-0">
                          <UserPlus size={16} className="text-[#C6A45D]" />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-sm text-[#F5F2EB]">{p.name}</p>
                          <p className="text-xs text-[#A5A5A5]/70 mt-0.5">
                            {p.email}{p.azienda ? ` · ${p.azienda}` : ""}
                          </p>
                        </div>
                        <span className="text-xs text-[#A5A5A5]/70">{formatDate(p.created_at)}</span>
                        <button onClick={() => eliminaPartner(p.email)} data-testid={`admin-partner-delete-${p.email}`} className={btnDanger}>
                          <Ban size={13} /> Revoca
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* DOCUMENTI */}
            {sezione === "documenti" && (
              <div data-testid="admin-sezione-documenti">
                <form onSubmit={uploadDocumento} data-testid="admin-doc-upload-form" className={cardCls + " p-6 sm:p-8 mb-6 space-y-5"}>
                  <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
                    <div className="flex-1">
                      <label htmlFor="doc-titolo" className="block eyebrow mb-3 !text-[10px]">Titolo documento *</label>
                      <input id="doc-titolo" data-testid="admin-doc-titolo" value={docTitolo} onChange={(e) => setDocTitolo(e.target.value)} placeholder="Es. Brochure istituzionale 2026" className={inputCls} />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="doc-file" className="block eyebrow mb-3 !text-[10px]">File (PDF, immagini, Excel — max 15 MB) *</label>
                      <input
                        id="doc-file"
                        data-testid="admin-doc-file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.txt,.csv,.xlsx,.docx"
                        onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                        className="w-full bg-[#1D1D1D] border border-[#A5A5A5]/25 text-[#A5A5A5] px-4 py-2.5 text-sm font-light file:mr-4 file:px-4 file:py-1.5 file:border-0 file:bg-[#C6A45D] file:text-[#080808] file:text-xs file:uppercase file:tracking-[0.15em] file:cursor-pointer focus:outline-none focus:border-[#E5C776] transition-colors duration-300"
                      />
                    </div>
                  </div>
                  <DestinatariPicker partners={partners} selected={docDest} onChange={setDocDest} testid="admin-doc-dest" />
                  <button type="submit" disabled={uploadingDoc} data-testid="admin-doc-upload-button" className={btnGold}>
                    <Upload size={14} /> {uploadingDoc ? "Caricamento..." : "Carica"}
                  </button>
                </form>
                {documenti.length === 0 && !loading ? (
                  <Empty text="Nessun documento caricato. I file caricati qui sono visibili solo ai partner approvati." />
                ) : (
                  <div className="space-y-3" data-testid="admin-documenti-list">
                    {documenti.map((d) => (
                      <div key={d.id} data-testid={`admin-documento-${d.id}`} className={cardCls + " p-5 flex flex-wrap items-center gap-4"}>
                        <FileText size={20} className="text-[#C6A45D] shrink-0" />
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-sm text-[#F5F2EB]">{d.titolo}</p>
                          <p className="text-xs text-[#A5A5A5]/70 mt-1">
                            {d.original_filename} · {(d.size / 1024).toFixed(0)} KB · {formatDate(d.created_at)}
                          </p>
                        </div>
                        <DestLabel destinatari={d.destinatari} />
                        <button onClick={() => deleteDocumento(d.id)} data-testid={`admin-doc-delete-${d.id}`} className={btnDanger}>
                          <Trash2 size={13} /> Elimina
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* OPPORTUNITÀ */}
            {sezione === "opportunita" && (
              <div data-testid="admin-sezione-opportunita">
                <form onSubmit={creaOpportunita} data-testid="admin-opp-form" className={cardCls + " p-6 sm:p-8 mb-6 space-y-5"}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="opp-titolo" className="block eyebrow mb-3 !text-[10px]">Titolo *</label>
                      <input id="opp-titolo" data-testid="admin-opp-titolo" required minLength={3} value={oppForm.titolo} onChange={(e) => setOppForm({ ...oppForm, titolo: e.target.value })} placeholder="Es. Nuovo boutique hotel a Milano" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="opp-divisione" className="block eyebrow mb-3 !text-[10px]">Business Unit</label>
                      <select id="opp-divisione" data-testid="admin-opp-divisione" value={oppForm.divisione} onChange={(e) => setOppForm({ ...oppForm, divisione: e.target.value })} className={inputCls + " appearance-none cursor-pointer"}>
                        <option value="">Trasversale</option>
                        {DIVISIONI.map((d) => (
                          <option key={d.id} value={d.nome}>Billions {d.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-5">
                    <div>
                      <label htmlFor="opp-descrizione" className="block eyebrow mb-3 !text-[10px]">Descrizione *</label>
                      <textarea id="opp-descrizione" data-testid="admin-opp-descrizione" required minLength={10} rows={3} value={oppForm.descrizione} onChange={(e) => setOppForm({ ...oppForm, descrizione: e.target.value })} placeholder="Descrivi l'opportunità per i partner..." className={inputCls + " resize-none"} />
                    </div>
                    <div>
                      <label htmlFor="opp-scadenza" className="block eyebrow mb-3 !text-[10px]">Scadenza</label>
                      <input id="opp-scadenza" data-testid="admin-opp-scadenza" value={oppForm.scadenza} onChange={(e) => setOppForm({ ...oppForm, scadenza: e.target.value })} placeholder="Es. 31/12/2026" className={inputCls} />
                    </div>
                  </div>
                  <DestinatariPicker partners={partners} selected={oppDest} onChange={setOppDest} testid="admin-opp-dest" />
                  <button type="submit" disabled={savingOpp} data-testid="admin-opp-submit" className={btnGold}>
                    <Plus size={14} /> {savingOpp ? "Pubblicazione..." : "Pubblica opportunità"}
                  </button>
                </form>
                {opportunita.length === 0 && !loading ? (
                  <Empty text="Nessuna opportunità pubblicata." />
                ) : (
                  <div className="space-y-3" data-testid="admin-opportunita-list">
                    {opportunita.map((o) => (
                      <div key={o.id} data-testid={`admin-opportunita-${o.id}`} className={cardCls + " p-5 sm:p-6"}>
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p className="font-display text-lg text-[#F5F2EB]">{o.titolo}</p>
                            <p className="text-xs text-[#C6A45D] tracking-[0.15em] uppercase mt-1">
                              {o.divisione ? `Billions ${o.divisione}` : "Trasversale"}
                              {o.scadenza ? ` · Scadenza ${o.scadenza}` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <DestLabel destinatari={o.destinatari} />
                            <button onClick={() => deleteOpportunita(o.id)} data-testid={`admin-opp-delete-${o.id}`} className={btnDanger}>
                              <Trash2 size={13} /> Elimina
                            </button>
                          </div>
                        </div>
                        <p className="mt-3 text-sm text-[#A5A5A5] font-light leading-relaxed">{o.descrizione}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* COMUNICAZIONI */}
            {sezione === "comunicazioni" && (
              <div data-testid="admin-sezione-comunicazioni">
                <form onSubmit={creaComunicazione} data-testid="admin-com-form" className={cardCls + " p-6 sm:p-8 mb-6 space-y-5"}>
                  <div>
                    <label htmlFor="com-titolo" className="block eyebrow mb-3 !text-[10px]">Titolo *</label>
                    <input id="com-titolo" data-testid="admin-com-titolo" required minLength={3} value={comForm.titolo} onChange={(e) => setComForm({ ...comForm, titolo: e.target.value })} placeholder="Es. Benvenuti nella piattaforma partner" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="com-testo" className="block eyebrow mb-3 !text-[10px]">Messaggio *</label>
                    <textarea id="com-testo" data-testid="admin-com-testo" required minLength={5} rows={3} value={comForm.testo} onChange={(e) => setComForm({ ...comForm, testo: e.target.value })} placeholder="Aggiornamento o comunicazione per i partner..." className={inputCls + " resize-none"} />
                  </div>
                  <DestinatariPicker partners={partners} selected={comDest} onChange={setComDest} testid="admin-com-dest" />
                  <button type="submit" disabled={savingCom} data-testid="admin-com-submit" className={btnGold}>
                    <Megaphone size={14} /> {savingCom ? "Pubblicazione..." : "Pubblica comunicazione"}
                  </button>
                </form>
                {comunicazioni.length === 0 && !loading ? (
                  <Empty text="Nessuna comunicazione pubblicata." />
                ) : (
                  <div className="space-y-3" data-testid="admin-comunicazioni-list">
                    {comunicazioni.map((c) => (
                      <div key={c.id} data-testid={`admin-comunicazione-${c.id}`} className={cardCls + " p-5 sm:p-6 flex flex-wrap items-start justify-between gap-4"}>
                        <div className="flex-1 min-w-[220px]">
                          <p className="font-display text-lg text-[#F5F2EB]">{c.titolo}</p>
                          <p className="text-xs text-[#A5A5A5]/70 mt-1">{formatDate(c.created_at)}</p>
                          <p className="mt-3 text-sm text-[#A5A5A5] font-light leading-relaxed">{c.testo}</p>
                        </div>
                        <div className="flex flex-col items-end gap-3">
                          <DestLabel destinatari={c.destinatari} />
                          <button onClick={() => deleteComunicazione(c.id)} data-testid={`admin-com-delete-${c.id}`} className={btnDanger}>
                            <Trash2 size={13} /> Elimina
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* MESSAGGI */}
            {sezione === "messaggi" && (
              <div data-testid="admin-sezione-messaggi">
                {contatti.length === 0 && !loading ? (
                  <Empty text="Nessun messaggio ricevuto finora." />
                ) : (
                  <div className="space-y-3" data-testid="admin-contatti-list">
                    {contatti.map((r) => {
                      const open = expandedMsg === r.id;
                      return (
                        <div key={r.id} data-testid={`admin-contatto-${r.id}`} className={cardCls}>
                          <div
                            className="flex flex-wrap items-center gap-4 p-5 cursor-pointer select-none"
                            onClick={() => setExpandedMsg(open ? null : r.id)}
                            data-testid={`admin-contatto-toggle-${r.id}`}
                          >
                            <ChevronRight size={18} className={`text-[#C6A45D] shrink-0 transition-transform duration-300 ${open ? "rotate-90" : ""}`} />
                            <div className="flex-1 min-w-[220px]">
                              <p className="font-display text-lg text-[#F5F2EB]">{r.nome}</p>
                              <p className="text-xs text-[#A5A5A5] mt-0.5">
                                {r.email}{r.azienda ? ` · ${r.azienda}` : ""}
                              </p>
                            </div>
                            <span className="inline-block px-3 py-1 border border-[#C6A45D]/40 text-[#C6A45D] text-[10px] tracking-[0.15em] uppercase">
                              {r.tipo}
                            </span>
                            <span className="text-xs text-[#A5A5A5]/70 hidden sm:inline">{formatDate(r.created_at)}</span>
                          </div>
                          {open && (
                            <div className="px-5 pb-5 pt-4 border-t border-[#C6A45D]/10">
                              {r.divisione && (
                                <p className="text-xs tracking-[0.2em] uppercase text-[#C6A45D]/80 mb-3">Business Unit: {r.divisione}</p>
                              )}
                              <p className="text-sm text-[#A5A5A5] font-light leading-relaxed">{r.messaggio}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
